/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  // eslint-disable-next-line class-methods-use-this
  kanbanParams({
    inboxId,
    teamId,
    status,
    assigneeType,
    sortBy,
    conversationType,
    labels,
    labelsAll,
    labelsNone,
    labelsAny,
    assigneeId,
    assignedTeamId,
    attention,
    createdFrom,
    createdTo,
    q,
    filterPayload,
  } = {}) {
    return {
      inbox_id: inboxId,
      team_id: teamId,
      status,
      assignee_type: assigneeType,
      sort_by: sortBy,
      conversation_type: conversationType,
      labels: labelsAny?.length ? labelsAny : labels,
      labels_all: labelsAll,
      labels_none: labelsNone,
      assignee_id: assigneeId,
      assigned_team_id: assignedTeamId,
      attention,
      created_from: createdFrom,
      created_to: createdTo,
      q,
      filter_payload: filterPayload,
    };
  }

  getLabelCounts({ signal, ...filters } = {}) {
    return axios.post(`${this.url}/label_counts`, this.kanbanParams(filters), {
      signal,
    });
  }

  getKanbanColumn({ label, page = 1, perPage = 25, signal, ...filters }) {
    const params = this.kanbanParams(filters);
    params.page = page;
    params.per_page = perPage;
    if (label === null) params.unlabeled = true;
    else
      params.labels_all = [...new Set([...(params.labels_all || []), label])];
    return axios.post(`${this.url}/kanban_column`, params, { signal });
  }

  moveKanbanCard({ conversationId, fromLabel, toLabel }) {
    return axios.post(`${this.url}/${conversationId}/kanban_move`, {
      from_label: fromLabel,
      to_label: toLabel,
    });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    conversationType,
    sortBy,
    updatedWithin,
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        updated_within: updatedWithin,
      },
    });
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      params: {
        page: payload.page,
      },
    });
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({ conversationId, status, snoozedUntil = null }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId, assigneeType }) {
    return axios.post(`${this.url}/${conversationId}/assignments`, {
      assignee_id: agentId,
      assignee_type: assigneeType,
    });
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  meta({ inboxId, status, assigneeType, labels, teamId, conversationType }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  getInboxAssistant(conversationId) {
    return axios.get(`${this.url}/${conversationId}/inbox_assistant`);
  }

  delete(conversationId) {
    return axios.delete(`${this.url}/${conversationId}`);
  }
}

export default new ConversationApi();
